import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, validateNewPassword } from "@/lib/password-policy";
import {
  classifyLinkError,
  cockpitNewLinkHref,
  INVITE_LINK_ERROR_TITLE,
  linkErrorTitle,
} from "@/lib/invite-link-error";
import { RequestNewLinkPanel } from "@/components/auth/RequestNewLinkPanel";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { evaluatePassword } from "@/lib/password-policy";

import { classifyPasswordUpdateError } from "@/lib/password-update-error";
import { LINK_CREDENTIAL_PARAMS, parseInviteLink } from "@/lib/invite-link";
import { useServerFn } from "@tanstack/react-start";
import { finalizeCustomerPassword } from "@/lib/customer-password.functions";

export const Route = createFileRoute("/cockpit_/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Passwort zurücksetzen – Cockpit" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CockpitResetPage,
});

type Status =
  | { kind: "confirm" }
  | { kind: "manual" }
  | { kind: "redeeming" }
  | { kind: "ready" }
  | { kind: "error"; msg: string };

function CockpitResetPage() {
  const navigate = useNavigate();
  const finalizePassword = useServerFn(finalizeCustomerPassword);

  // Der Link wird nur GELESEN — kein Einlösen beim Rendern (Ticket K1).
  const parsed = useMemo(
    () => parseInviteLink(typeof window !== "undefined" ? window.location.href : ""),
    [],
  );

  const [status, setStatus] = useState<Status>(() => {
    if (parsed.errorDescription) {
      return {
        kind: "error",
        msg: classifyLinkError(decodeURIComponent(parsed.errorDescription)).msg,
      };
    }
    if (parsed.hasCredentials) return { kind: "confirm" };
    return { kind: "manual" };
  });
  const [manualCode, setManualCode] = useState("");
  const [manualEmail, setManualEmail] = useState(parsed.email ?? "");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const redeemedRef = useRef(false);

  const isInvite = parsed.isInvite;
  const pwRulesMet = evaluatePassword(password, confirmPw).allMet;

  // Bereits bestehende Session (z. B. Reload nach erfolgreichem Einlösen):
  // reines Lesen, verbraucht keinen Token.
  useEffect(() => {
    if (status.kind !== "manual") return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      if (data.session) setStatus({ kind: "ready" });
    })();
    return () => {
      cancelled = true;
    };
  }, [status.kind]);

  const stripCredentials = () => {
    const cleaned = new URL(window.location.href);
    for (const key of LINK_CREDENTIAL_PARAMS) cleaned.searchParams.delete(key);
    window.history.replaceState({}, "", cleaned.pathname + cleaned.search);
  };

  const failLink = async (rawMessage: string | null | undefined) => {
    const { msg } = classifyLinkError(rawMessage);
    try {
      await supabase.auth.signOut();
    } catch {
      /* ignore */
    }
    setStatus({ kind: "error", msg });
  };

  /** Einlösen — ausschließlich durch bewusste Nutzeraktion aufgerufen. */
  const redeem = async (code?: string, email?: string) => {
    if (redeemedRef.current) return;
    redeemedRef.current = true;
    setStatus({ kind: "redeeming" });
    const otpType = parsed.authFlow === "invite" ? "invite" : "recovery";

    try {
      if (code && email) {
        const { error: otpErr } = await supabase.auth.verifyOtp({
          email,
          token: code,
          type: otpType,
        });
        if (otpErr) throw new Error(otpErr.message);
      } else if (parsed.tokenHash) {
        const { error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: parsed.tokenHash,
          type: otpType,
        });
        if (otpErr) throw new Error(otpErr.message);
      } else if (parsed.otpCode && parsed.email) {
        const { error: otpErr } = await supabase.auth.verifyOtp({
          email: parsed.email,
          token: parsed.otpCode,
          type: otpType,
        });
        if (otpErr) throw new Error(otpErr.message);
      } else if (parsed.accessToken && parsed.refreshToken) {
        const { error: sessErr } = await supabase.auth.setSession({
          access_token: parsed.accessToken,
          refresh_token: parsed.refreshToken,
        });
        if (sessErr) throw new Error(sessErr.message);
      } else if (parsed.pkceCode) {
        const { error: codeErr } = await supabase.auth.exchangeCodeForSession(
          parsed.pkceCode,
        );
        if (codeErr) throw new Error(codeErr.message);
      } else {
        throw new Error("invalid link");
      }
    } catch (err) {
      redeemedRef.current = false;
      stripCredentials();
      await failLink(err instanceof Error ? err.message : String(err));
      return;
    }

    stripCredentials();
    setStatus({ kind: "ready" });
  };

  const onManualSubmit = (e: FormEvent) => {
    e.preventDefault();
    const code = manualCode.trim();
    const email = manualEmail.trim().toLowerCase();
    if (!code || !email) {
      setError("Bitte E-Mail-Adresse und Code aus der E-Mail eingeben.");
      return;
    }
    setError(null);
    void redeem(code, email);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);

    const problem = validateNewPassword(password, confirmPw);
    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);

    // Vor dem Schreiben sicherstellen, dass die Link-Session noch steht.
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData.user) {
      setBusy(false);
      const mapped = classifyPasswordUpdateError(userErr?.message ?? "auth session missing");
      setStatus({ kind: "error", msg: mapped.msg });
      return;
    }

    // Passwort serverseitig setzen, damit gleichzeitig der Onboarding-Zeitstempel
    // (password_set_at) geschrieben und die Einladung als erledigt markiert wird.
    const accountEmail = (userData.user.email ?? "").toLowerCase();
    let updErrMessage: string | null = null;
    try {
      await finalizePassword({ data: { password } });
    } catch (err) {
      updErrMessage = err instanceof Error ? err.message : String(err);
    }

    if (updErrMessage) {
      setBusy(false);
      const mapped = classifyPasswordUpdateError(updErrMessage);
      if (mapped.requiresNewLink) {
        try {
          await supabase.auth.signOut();
        } catch {
          /* ignore */
        }
        setStatus({ kind: "error", msg: mapped.msg });
        return;
      }
      setError(mapped.msg);
      return;
    }

    // Ticket K3: Kein zweiter Login-Schritt. Die alten Sitzungen (inkl. Link-
    // Sitzung) wurden serverseitig beendet; hier wird sofort und unsichtbar mit
    // dem gerade gesetzten Passwort eine frische Sitzung erzeugt.
    let signedIn = false;
    if (accountEmail) {
      const { data: signInData, error: signInErr } = await supabase.auth.signInWithPassword({
        email: accountEmail,
        password,
      });
      signedIn = !signInErr && Boolean(signInData.session);
    }
    setBusy(false);
    setDone(true);

    if (!signedIn) {
      // Rückfall: Anmeldung mit vorausgefüllter E-Mail, Passwort ist gesetzt.
      setTimeout(
        () =>
          navigate({
            to: "/cockpit/login",
            search: { redirect: "/cockpit", email: accountEmail || undefined },
            replace: true,
          }),
        1200,
      );
      return;
    }

    // Falls für das Konto eine zweite Stufe eingerichtet ist, greift sie auch
    // hier: die Sitzung bleibt auf aal1 und muss erst bestätigt werden.
    try {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.nextLevel === "aal2" && aal.nextLevel !== aal.currentLevel) {
        navigate({ to: "/cockpit/login", search: { redirect: "/cockpit" }, replace: true });
        return;
      }
    } catch {
      /* keine MFA konfiguriert – weiter ins Cockpit */
    }

    setTimeout(() => navigate({ to: "/cockpit", replace: true }), 800);
  };


  const heading = isInvite ? "Willkommen im Kampagnencockpit" : "Neues Passwort setzen";
  const intro = isInvite
    ? "Legen Sie ein sicheres Passwort fest, um Ihren Cockpit-Zugang zu aktivieren."
    : "Legen Sie ein neues Passwort für Ihren Cockpit-Zugang fest.";
  const cta = isInvite ? "Zugang aktivieren" : "Passwort speichern";
  const confirmCta = isInvite
    ? "Passwort jetzt festlegen"
    : "Neues Passwort festlegen";
  const newLinkHref = cockpitNewLinkHref(parsed.email ?? manualEmail);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">{heading}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{intro}</p>

          {status.kind === "confirm" && (
            <div className="mt-8 space-y-4">
              <p className="text-sm text-muted-foreground">
                Klicken Sie hier, um Ihr Passwort festzulegen. Ihr Link bleibt bis
                dahin unverbraucht.
              </p>
              <button
                type="button"
                onClick={() => void redeem()}
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                {confirmCta}
              </button>
            </div>
          )}

          {status.kind === "redeeming" && (
            <p className="mt-8 text-sm text-muted-foreground">Link wird geprüft …</p>
          )}

          {status.kind === "manual" && (
            <form onSubmit={onManualSubmit} className="mt-8 space-y-4" noValidate>
              <p className="text-sm text-muted-foreground">
                Bitte öffnen Sie den Link direkt aus Ihrer E-Mail. Alternativ können
                Sie den Code aus der E-Mail hier eingeben.
              </p>
              <div>
                <label htmlFor="manual-email" className="block text-sm font-semibold">
                  E-Mail-Adresse
                </label>
                <input
                  id="manual-email"
                  type="email"
                  autoComplete="email"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label htmlFor="manual-code" className="block text-sm font-semibold">
                  Code aus der E-Mail
                </label>
                <input
                  id="manual-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                {confirmCta}
              </button>
              <Link
                to="/cockpit/login"
                className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Zur Anmeldung
              </Link>
            </form>
          )}

          {status.kind === "error" && (
            <div className="mt-8 space-y-3 rounded-md border border-border bg-secondary p-4 text-sm">
              <p className="font-semibold text-destructive">{linkErrorTitle(status.msg)}</p>
              <p className="text-muted-foreground">{status.msg}</p>
              <RequestNewLinkPanel area="cockpit" initialEmail={parsed.email ?? manualEmail} />
              <a
                href={newLinkHref}
                className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Passwort vergessen? Über die Anmeldung anfordern
              </a>
              <Link
                to="/cockpit/login"
                className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Zur Anmeldung
              </Link>
            </div>
          )}


          {status.kind === "ready" && done && (
            <p className="mt-8 text-sm text-primary">
              Passwort gespeichert. Sie werden weitergeleitet …
            </p>
          )}

          {status.kind === "ready" && !done && (
            <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
              <div>
                <label htmlFor="password" className="block text-sm font-semibold">
                  {isInvite ? "Passwort" : "Neues Passwort"}{" "}
                  <span className="font-normal text-muted-foreground">({PASSWORD_HINT})</span>
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-describedby="password-requirements"
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
                <PasswordRequirements password={password} confirm={confirmPw} />
              </div>
              <div>
                <label htmlFor="confirm" className="block text-sm font-semibold">
                  Passwort wiederholen
                </label>
                <input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy || !pwRulesMet}
                aria-describedby="password-requirements"
                className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {busy ? "Speichern …" : cta}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
